// React
import { useMemo } from "react"

// I18n
import { useLang } from "@/context/lang"
import { useTrans } from "@/lib/i18n"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"
import { endDateStatistics } from "@/constants/constants"

// Router
import { Routes } from "@/constants/routes"

// UI
import { CardWrapper, CardBody } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { MultiGraph } from "@/components/graphs/types/bar-line/MultiGraph"
import { Link } from "@/components/Anchor"

// Visx
import { TickRendererProps } from "@visx/axis"
import { Text } from "@visx/text"

// Query
import { useFinancialPerformanceByYearQuery } from "@/api/graphql"

/**
 * FinancialPerformanceGraph
 * @returns
 */
export function FinancialPerformanceGraph() {
	// Constants
	const DateTime = useLocalizedDateTime()

	// Translate
	const { formatNumber, formatCurrency } = useLang()
	const t = useTrans("dashboard")

	// Query
	const { data } = useFinancialPerformanceByYearQuery()

	const financialPerformanceData =
		data?.platform_statistics?.financial_performance ?? []

	const barData = useMemo(
		() => [
			{
				id: "capital_at_risk",
				variant: "primary" as const,
				data: financialPerformanceData.map((item) => ({
					x: item?.year?.toString() ?? "0",
					y: item?.capital_at_risk ?? 0,
				})),
			},
		],
		[financialPerformanceData],
	)

	const lineData = useMemo(
		() => [
			{
				id: "interest_rate",
				variant: "primary" as const,
				data: financialPerformanceData.map((item) => ({
					x: item?.year?.toString() ?? "0",
					y: item?.interest_rate ?? 0,
				})),
			},
		],
		[financialPerformanceData],
	)

	const prepareTooltipValues = useMemo(
		() => (year: string) => {
			const yearData = financialPerformanceData.find(
				(d) => d?.year?.toString() === year,
			)
			if (!yearData) return []

			return [
				{
					id: "capital_at_risk",
					value: formatCurrency(yearData.capital_at_risk ?? 0, {
						maximumFractionDigits: 0,
					}),
					variant: "primary" as const,
					colour: "#FFD900",
				},
				{
					id: "interest_rate",
					value: `${formatNumber(yearData.interest_rate ?? 0)}%`,
					variant: "primary" as const,
					colour: "#149AE5",
				},
			]
		},
		[financialPerformanceData, formatNumber],
	)

	const prepareTooltipLabel = useMemo(() => (year: string) => year, [])

	// Custom tick formatter for capital_at_risk y-axis (left axis)
	const CapitalAtRiskTickComponent = useMemo(
		() =>
			function CapitalAtRiskTick({
				formattedValue,
				...props
			}: TickRendererProps) {
				const value = parseFloat(formattedValue as string)

				// Format the display value
				let displayValue = ""
				if (value === 0) {
					displayValue = "0M"
				} else if (value >= 1000000) {
					displayValue = `${Math.round(value / 1000000)}M`
				} else if (value >= 1000) {
					displayValue = `${Math.round(value / 1000)}K`
				} else {
					displayValue = `${value}M`
				}

				return <Text {...props}>{displayValue}</Text>
			},
		[],
	)

	// Custom tick formatter for x-axis (years)
	const YearTickComponent = useMemo(
		() =>
			function YearTick({ formattedValue, ...props }: TickRendererProps) {
				// Just display the year as-is
				return <Text {...props}>{formattedValue}</Text>
			},
		[],
	)

	// Custom tick formatter for interest_rate y-axis (right axis)
	const RightAxisTickComponent = useMemo(
		() =>
			function RightAxisTick({
				formattedValue,
				...props
			}: TickRendererProps) {
				const value = parseFloat(formattedValue as string)

				// Format as percentage with 2 decimal places
				const displayValue = `${value.toFixed(2)}%`

				return <Text {...props}>{displayValue}</Text>
			},
		[],
	)

	return (
		<CardWrapper className="mb-4">
			<CardBody>
				<Heading as="h3" className="mb-6">
					{t(
						"dashboard:dashboard.statistics.financial_performance.title",
					)}
				</Heading>
				<p className="mb-4 text-sm text-gray-600">
					{t("dashboard.statistics.platform.as_per_date", {
						date: DateTime.fromJSDate(
							endDateStatistics,
						).toLocaleString(),
					})}
				</p>
			</CardBody>
			<div className="h-96 w-full">
				<MultiGraph
					barDatas={barData}
					lineDatas={lineData}
					lineYDomain={[1, 5]}
					prepareTooltipValues={prepareTooltipValues}
					prepareTooltipLabel={prepareTooltipLabel}
					xTickComponent={CapitalAtRiskTickComponent}
					yTickComponent={YearTickComponent}
					rightYTickComponent={RightAxisTickComponent}
				/>
			</div>

			<div className="flex flex-col items-center py-4">
				<p className="m-4">
					{t(
						"dashboard:dashboard.statistics.financial_performance.description",
					)}
				</p>
				<Link href={Routes.StatisticsDefaultRate}>
					{t("dashboard:dashboard.statistics.default_rate.title")}
				</Link>
			</div>
		</CardWrapper>
	)
}
